import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { FaSearch, FaTh, FaList, FaFilter } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
//
import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import SearchOptionView from './components/SearchOptionView';
import ListView from '../../components/ui/ListView';
import Dropdown from '../../components/ui/Dropdown';
import Breadcrumb from '../../components/Breadcrumb';
import { Input, BodyText, Title } from '../../components/ui';
import ProductItem from './components/ProductItem';
import Pagination from '../../components/Pagenation';
import MobileFilterDrawer from '../../components/MobileFilterDrawer';
//
import { perPageOptions, sortOptions } from '../../dummy';
import {
  getProductList,
  getProductsByBrandTag,
  getProductByBarcode,
  getProductBySku,
  getProductByTitle,
  getProductByShortDescription,
  getProductByDetailedDescription,
} from '../../api/services/productService';
import { setShowLoading } from '../../redux/systemInfo';
import { urlToCategory, categoryToUrl } from '../../utils';
import { getMenuList } from '../../api/services/systemService';
import {
  parseHashParams,
  updateHashUrl,
  navigateToHash,
} from '../../utils/hashRouter';

const Container = styled.div`
  display: flex;
  justify-content: center;
  padding: 5rem 0;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 2rem 0;
  }
`;

const SearchOptionContainer = styled.div`
  width: 18%;
  max-width: 18%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchResultContainer = styled.div`
  width: 67%;
  max-width: 67%;

  @media (max-width: 768px) {
    width: 80%;
    max-width: 80%;
    padding: 0 2rem;
  }

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchBar = styled.div`
  display: flex;
  width: 30%;
  height: 50px;
  border-radius: 5px;
  align-items: center;
  border: 2px solid ${(props) => props.theme.colors.subBackground};
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    height: 40px;
    margin-top: 10px;
    padding: 0 2rem;
    box-sizing: border-box;
  }

  @media (max-width: 480px) {
    height: 35px;
    padding: 0 0.5rem;
  }
`;

const SearchButton = styled.button`
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 50px;
  height: 50px;
  border: none;
  background: none;
  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 30px 0;
  border-radius: 5px;
  gap: 15px;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    padding: 15px 0;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px 0;
    gap: 8px;
  }
`;

const RowButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 35px;
  height: 35px;
  &:hover {
    opacity: 0.8;
  }
`;

const TotalContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.subBackground};
  padding: 10px;
  font-size: 0.8rem;
`;

const homeCategory = { title: 'Home', link: '/' };

export default function SearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);

  const theme = useTheme();

  const [numColumns, setNumColumns] = useState(4);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [keyword, setKeyword] = useState('');

  const [totalProduct, setTotalProduct] = useState(0);
  const [productList, setProductList] = useState([]);
  const [categoryList, setCategoryList] = useState([homeCategory]);

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [perPage, setPerPage] = useState(perPageOptions[0]);
  const [sortBy, setSortBy] = useState(sortOptions[0]);
  const [displayTitle, setDisplayTitle] = useState('');
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const [menuLevel, setMenuLevel] = useState(null); // Store the menu level

  // AbortController to cancel previous requests
  const abortControllerRef = useRef(null);
  // Track the last request parameters to prevent duplicate calls
  const lastRequestParamsRef = useRef(null);

  // Parse hash-based URL parameters
  const { path, params } = parseHashParams();
  const category = params.get('category');
  const brand = params.get('brand');
  const page = params.get('page');
  const searchKeyword = params.get('keyword') || '';
  const subcategory = params.get('subcategory') || '';
  const barcode = params.get('barcode') || '';
  const sku = params.get('sku') || '';
  const filterParam = params.get('filter');

  // Initialize currentPage state
  const [currentPage, setCurrentPage] = useState(page ? Number(page) : 1);

  // Save initial filter param to prevent premature clearing
  const [initialFilterParam] = useState(filterParam);

  // Update keyword state when URL keyword or subcategory changes
  // Subcategory takes precedence over keyword if both are present
  useEffect(() => {
    const effectiveKeyword = subcategory || searchKeyword;
    setKeyword(effectiveKeyword);
  }, [searchKeyword, subcategory]);

  // Clear sessionStorage and localStorage on mount to prevent stale data
  useEffect(() => {
    const storedState = sessionStorage.getItem('searchState');
    if (storedState) {
      sessionStorage.removeItem('searchState');
    }
    // Clear any category-related localStorage keys that might cause issues
    const keysToCheck = [
      'category',
      'lastCategory',
      'collection',
      'selectedCategoryId',
    ];
    keysToCheck.forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  // Cleanup: Cancel any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch display title and breadcrumb path when category changes or when they're not available
  useEffect(() => {
    const fetchCategoryInfo = async () => {
      // Check if we have state from navigation
      if (location.state?.displayTitle && location.state?.breadcrumbPath) {
        setDisplayTitle(location.state.displayTitle);
        setBreadcrumbPath(location.state.breadcrumbPath);
        setMenuLevel(location.state.menuLevel || null); // Capture menuLevel from navigation
        return;
      }

      // Check sessionStorage
      try {
        const storedState = sessionStorage.getItem('searchState');
        if (storedState) {
          const parsed = JSON.parse(storedState);
          if (parsed.displayTitle && parsed.breadcrumbPath) {
            setDisplayTitle(parsed.displayTitle);
            setBreadcrumbPath(parsed.breadcrumbPath);
            setMenuLevel(parsed.menuLevel || null); // Capture menuLevel from storage
            return;
          }
        }
      } catch (e) {
        console.error('Error parsing searchState:', e);
      }

      // If we don't have state, fetch from menu list
      if (category) {
        try {
          const result = await getMenuList();
          const categoryMap = {};

          // Build category map with parent relationships
          result.menuList.forEach((cat) => {
            categoryMap[cat.idWebCate] = {
              id: cat.idWebCate,
              title: cat.cateNm,
              cateNm: cat.cateNm,
              parentId: cat.pCode,
            };
          });

          // Build hierarchical URL key for each item (same logic as MenuBar)
          const buildUrlKey = (menuItem) => {
            const pathNames = [];
            let current = menuItem;

            while (current) {
              const urlPart = current.cateNm
                .toLowerCase()
                .replace(/[&]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
              pathNames.unshift(urlPart);
              current = categoryMap[current.parentId];
            }

            return pathNames.join('-');
          };

          // Find the category that matches the URL
          const targetCategory = Object.values(categoryMap).find(
            (cat) => buildUrlKey(cat) === category,
          );

          if (targetCategory) {
            // Build breadcrumb path
            const path = [];
            let current = targetCategory;

            while (current) {
              const urlKey = buildUrlKey(current);
              path.unshift({
                title: current.title,
                link: `#/search?category=${encodeURIComponent(urlKey)}`,
              });
              current = categoryMap[current.parentId];
            }

            setDisplayTitle(targetCategory.title);
            setBreadcrumbPath(path);
          } else {
            // Fallback if category not found in menu
            const fallbackTitle = urlToCategory(category);
            setDisplayTitle(fallbackTitle);
            setBreadcrumbPath([]);
          }
        } catch (error) {
          console.error('Error fetching category info:', error);
          // Fallback
          const fallbackTitle = urlToCategory(category);
          setDisplayTitle(fallbackTitle);
          setBreadcrumbPath([]);
        }
      }
    };

    fetchCategoryInfo();
  }, [category, location.state]);

  // Call API when category, keyword, subcategory, brand, barcode, sku, or page changes
  // Also depend on displayTitle to ensure category info is loaded first
  useEffect(() => {
    if (category || brand || searchKeyword || barcode || sku) {
      // Only fetch products if we have displayTitle (category info loaded) or it's not a category search
      if (!category || displayTitle) {
        // Create a unique key for this request to prevent duplicate calls
        const requestKey = JSON.stringify({
          category,
          brand,
          searchKeyword,
          subcategory,
          barcode,
          sku,
          currentPage,
          perPage: perPage.value,
          sortBy: sortBy.value,
          displayTitle,
        });

        // Only make request if parameters have actually changed
        if (lastRequestParamsRef.current !== requestKey) {
          lastRequestParamsRef.current = requestKey;
          getDbProductList();
        }
      }
    }
  }, [
    category,
    brand,
    searchKeyword,
    subcategory,
    barcode,
    sku,
    currentPage,
    selectedFilters,
    displayTitle, // Add displayTitle as dependency to ensure category info is loaded
    perPage, // Re-fetch when items per page changes
    sortBy, // Re-fetch when sort option changes
  ]);

  // Function to fetch category hierarchy from backend
  const fetchCategoryHierarchy = async (categoryName) => {
    try {
      const result = await getMenuList();
      const categoryMap = {};

      // Build category map with parent relationships
      result.menuList.forEach((cat) => {
        categoryMap[cat.idWebCate] = {
          id: cat.idWebCate,
          title: cat.cateNm,
          link: `#/search?category=${categoryToUrl(cat.cateNm)}`,
          parentId: cat.pCode,
        };
      });

      // Find the target category by matching the URL-friendly version
      const targetCategory = Object.values(categoryMap).find(
        (cat) => categoryToUrl(cat.title) === categoryName,
      );

      if (targetCategory) {
        // Build breadcrumb path from target category up to root
        const path = [];
        let current = targetCategory;

        while (current) {
          path.unshift({
            title: current.title,
            link: current.link,
          });
          current = categoryMap[current.parentId];
        }

        return path;
      }

      return [];
    } catch (error) {
      console.error('Error fetching category hierarchy:', error);
      return [];
    }
  };

  // Function to build complete breadcrumb path including keyword
  const buildCompleteBreadcrumbPath = async (categoryName, keyword) => {
    try {
      const result = await getMenuList();
      const categoryMap = {};

      // Build category map with parent relationships
      result.menuList.forEach((cat) => {
        categoryMap[cat.idWebCate] = {
          id: cat.idWebCate,
          title: cat.cateNm,
          link: `#/search?category=${categoryToUrl(cat.cateNm)}`,
          parentId: cat.pCode,
        };
      });

      // Find the target category by matching the URL-friendly version
      const targetCategory = Object.values(categoryMap).find(
        (cat) => categoryToUrl(cat.title) === categoryName,
      );

      if (targetCategory) {
        // Build breadcrumb path from target category up to root
        const path = [];
        let current = targetCategory;

        while (current) {
          path.unshift({
            title: current.title,
            link: current.link,
          });
          current = categoryMap[current.parentId];
        }

        // If we have a keyword, we need to find where it fits in the hierarchy
        if (keyword) {
          // Look for the keyword in the menu structure
          const keywordCategory = Object.values(categoryMap).find(
            (cat) => categoryToUrl(cat.title) === keyword,
          );

          if (keywordCategory) {
            // Check if the keyword is a child of the target category
            let isChild = false;
            let current = keywordCategory;
            while (current && !isChild) {
              if (current.id === targetCategory.id) {
                isChild = true;
              }
              current = categoryMap[current.parentId];
            }

            if (isChild) {
              // Add the keyword as the final item
              path.push({
                title: keywordCategory.title,
                link: `#/search?category=${categoryName}&keyword=${keyword}`,
              });
            }
          } else {
            // Keyword not found in menu structure, add it as a simple item
            path.push({
              title: urlToCategory(keyword),
              link: `#/search?category=${categoryName}&keyword=${keyword}`,
            });
          }
        }

        return path;
      }

      return [];
    } catch (error) {
      console.error('Error building complete breadcrumb path:', error);
      return [];
    }
  };

  useEffect(() => {
    const { params } = parseHashParams();
    const pageFromUrl = params.get('page');
    const keywordFromUrl = params.get('keyword') || '';
    setCurrentPage(pageFromUrl ? Number(pageFromUrl) : 1); // fallback 1
    setKeyword(keywordFromUrl);
  }, [window.location.hash]); // Only update state, don't call API here

  // Handle filter changes from URL parameters
  useEffect(() => {
    const { params } = parseHashParams();
    const filterParam = params.get('filter');
    if (!filterParam) {
      setSelectedFilters([]);
    }
  }, [window.location.hash]);

  // Update URL when filters change (but don't trigger API call)
  useEffect(() => {
    if (selectedFilters.length === 0) {
      // Only remove filter from URL if there wasn't one when the page initially loaded
      // This prevents clearing a filter that just hasn't been restored yet
      if (!initialFilterParam) {
        updateHashUrl('/search', {
          category,
          brand,
          subcategory: subcategory,
          keyword: searchKeyword,
          page: currentPage || 1,
          filter: undefined,
        });
      }
    } else if (selectedFilters.length > 0) {
      const filterString = selectedFilters.map((item) => item.id).join(',');
      updateHashUrl('/search', {
        category,
        brand,
        subcategory: subcategory,
        keyword: searchKeyword,
        page: currentPage || 1,
        filter: filterString,
      });
    }
  }, [selectedFilters, initialFilterParam]);

  // Helper function to process search results
  const processSearchResults = (result, searchType, searchTerm) => {
    // Check if the response indicates success
    if (result.is_success === false) {
      console.error(`Search error (${searchType}):`, result.message);
      setProductList([]);
      setTotalProduct(0);
      dispatch(setShowLoading({ showLoading: false }));
      return false;
    }

    if (currentPage === 1) {
      setTotalProduct(result.total || 0);
    }

    // Filter out products that don't have any image path
    let filteredProductList = (result.productList || []).filter(
      (product) => product.image || product.filepath1,
    );

    // Apply availability filters (client-side)
    const availabilityFilters = selectedFilters.filter(
      (f) => f.name === 'availability',
    );
    if (availabilityFilters.length > 0) {
      filteredProductList = filteredProductList.filter((product) => {
        const inStock = product.avaQty > 0;
        return availabilityFilters.some((filter) => {
          if (filter.id === 'availability_in_stock') return inStock;
          if (filter.id === 'availability_out_of_stock') return !inStock;
          return true;
        });
      });
    }

    setProductList(filteredProductList);
    dispatch(setShowLoading({ showLoading: false }));
    return filteredProductList.length > 0;
  };

  // Helper function to implement search hierarchy for keyword searches
  const searchWithHierarchy = async (searchTerm, signal) => {
    if (!searchTerm || !searchTerm.trim()) {
      return false;
    }

    const trimmedTerm = searchTerm.trim();
    const brandId = userInfo.brandId || 1;
    const storeId = userInfo.idStore || 1;
    const idCustomer = userInfo.idCustomer || 1;
    const priceLev = '01'; // Always use price level 01

    // 1. Try barcode search (if it looks like a barcode: 8-14 digits)
    if (/^\d{8,14}$/.test(trimmedTerm)) {
      try {
        const result = await getProductByBarcode(
          trimmedTerm,
          brandId,
          storeId,
          idCustomer,
          currentPage,
          perPage,
          priceLev,
          sortBy,
          signal,
        );
        if (
          result &&
          result.is_success !== false &&
          result.productList &&
          result.productList.length > 0
        ) {
          setCategoryList([
            homeCategory,
            {
              title: `Barcode: ${trimmedTerm}`,
              link: `#/search?barcode=${encodeURIComponent(trimmedTerm)}`,
            },
          ]);
          return processSearchResults(result, 'barcode', trimmedTerm);
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return false; // Request was cancelled, don't process
        }
        console.error('Barcode search error:', error);
      }
    }

    // 2. Try SKU search (if it looks like a SKU: has letters, numbers, and separator)
    const hasLetters = /[A-Z]/i.test(trimmedTerm);
    const hasNumbers = /\d/.test(trimmedTerm);
    const hasSeparator = /[-._]/.test(trimmedTerm);
    const isValidSkuFormat = /^[A-Z0-9._-]{3,20}$/i.test(trimmedTerm);

    if (isValidSkuFormat && hasLetters && hasNumbers && hasSeparator) {
      try {
        const result = await getProductBySku(
          trimmedTerm,
          brandId,
          storeId,
          idCustomer,
          currentPage,
          perPage,
          priceLev,
          sortBy,
          signal,
        );
        if (
          result &&
          result.is_success !== false &&
          result.productList &&
          result.productList.length > 0
        ) {
          setCategoryList([
            homeCategory,
            {
              title: `SKU: ${trimmedTerm}`,
              link: `#/search?sku=${encodeURIComponent(trimmedTerm)}`,
            },
          ]);
          return processSearchResults(result, 'sku', trimmedTerm);
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return false; // Request was cancelled, don't process
        }
        console.error('SKU search error:', error);
      }
    }

    // 3. Try title/item name search
    try {
      const result = await getProductByTitle(
        trimmedTerm,
        brandId,
        storeId,
        idCustomer,
        currentPage,
        perPage,
        priceLev,
        sortBy,
        signal,
      );
      if (
        result &&
        result.is_success !== false &&
        result.productList &&
        result.productList.length > 0
      ) {
        const capitalizedKeyword =
          trimmedTerm.charAt(0).toUpperCase() + trimmedTerm.slice(1);
        setCategoryList([
          homeCategory,
          {
            title: capitalizedKeyword,
            link: `#/search?keyword=${encodeURIComponent(trimmedTerm)}`,
          },
        ]);
        return processSearchResults(result, 'title', trimmedTerm);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return false; // Request was cancelled, don't process
      }
      console.error('Title search error:', error);
    }

    // 4. Try short description search
    try {
      const result = await getProductByShortDescription(
        trimmedTerm,
        brandId,
        storeId,
        idCustomer,
        currentPage,
        perPage,
        priceLev,
        sortBy,
        signal,
      );
      if (
        result &&
        result.is_success !== false &&
        result.productList &&
        result.productList.length > 0
      ) {
        const capitalizedKeyword =
          trimmedTerm.charAt(0).toUpperCase() + trimmedTerm.slice(1);
        setCategoryList([
          homeCategory,
          {
            title: capitalizedKeyword,
            link: `#/search?keyword=${encodeURIComponent(trimmedTerm)}`,
          },
        ]);
        return processSearchResults(result, 'shortDescription', trimmedTerm);
      }
    } catch (error) {
      console.error('Short description search error:', error);
    }

    // 5. Try detailed description search
    try {
      const result = await getProductByDetailedDescription(
        trimmedTerm,
        brandId,
        storeId,
        idCustomer,
        currentPage,
        perPage,
        priceLev,
        sortBy,
      );
      if (
        result &&
        result.is_success !== false &&
        result.productList &&
        result.productList.length > 0
      ) {
        const capitalizedKeyword =
          trimmedTerm.charAt(0).toUpperCase() + trimmedTerm.slice(1);
        setCategoryList([
          homeCategory,
          {
            title: capitalizedKeyword,
            link: `#/search?keyword=${encodeURIComponent(trimmedTerm)}`,
          },
        ]);
        return processSearchResults(result, 'detailedDescription', trimmedTerm);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return false; // Request was cancelled, don't process
      }
      console.error('Detailed description search error:', error);
    }

    // No results found in any search type
    setProductList([]);
    setTotalProduct(0);
    dispatch(setShowLoading({ showLoading: false }));
    return false;
  };

  const getDbProductList = async () => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    dispatch(setShowLoading({ showLoading: true }));

    try {
      // Handle explicit SKU search
      if (sku) {
        setCategoryList([
          homeCategory,
          {
            title: `SKU: ${sku}`,
            link: `#/search?sku=${encodeURIComponent(sku)}`,
          },
        ]);

        const result = await getProductBySku(
          sku,
          userInfo.brandId || 1,
          userInfo.idStore || 1,
          userInfo.idCustomer || 1,
          currentPage,
          perPage,
          '01', // Always use price level 01
          sortBy,
          signal,
        );

        // Check if request was aborted
        if (signal.aborted) {
          return;
        }

        processSearchResults(result, 'sku', sku);
        return;
      }

      // Handle explicit barcode search
      if (barcode) {
        setCategoryList([
          homeCategory,
          {
            title: `Barcode: ${barcode}`,
            link: `#/search?barcode=${encodeURIComponent(barcode)}`,
          },
        ]);

        const result = await getProductByBarcode(
          barcode,
          userInfo.brandId || 1,
          userInfo.idStore || 1,
          userInfo.idCustomer || 1,
          currentPage,
          perPage,
          '01', // Always use price level 01
          sortBy,
          signal,
        );

        // Check if request was aborted
        if (signal.aborted) {
          return;
        }

        processSearchResults(result, 'barcode', barcode);
        return;
      }

      // Handle keyword search with hierarchy (barcode → sku → title → short desc → detailed desc)
      // Apply hierarchy for keyword-only searches (no category or brand)
      if (searchKeyword && !category && !brand) {
        const found = await searchWithHierarchy(searchKeyword, signal);
        if (found) {
          return;
        }
        // If no results found in hierarchy, fall through to regular keyword search
      }

      // Handle brand filtering
      if (brand) {
        // Set breadcrumb for brand
        const brandTitle = decodeURIComponent(brand);
        setCategoryList([
          homeCategory,
          {
            title: `${brandTitle}`,
            link: `/search?brand=${brand}`,
          },
        ]);

        const result = await getProductsByBrandTag(
          brandTitle,
          userInfo.idStore,
          userInfo.idCustomer,
          currentPage,
          perPage,
          '01', // Always use price level 01
          sortBy,
          signal,
        );

        // Check if request was aborted
        if (signal.aborted) {
          return;
        }

        if (currentPage === 1) {
          // page 가 1일때만 total 나옴
          setTotalProduct(result.total);
        }

        // Filter out products that don't have any image path
        let filteredProductList = result.productList.filter(
          (product) => product.image || product.filepath1,
        );

        // Apply availability filters (client-side)
        const availabilityFilters = selectedFilters.filter(
          (f) => f.name === 'availability',
        );
        if (availabilityFilters.length > 0) {
          filteredProductList = filteredProductList.filter((product) => {
            const inStock = product.avaQty > 0;
            return availabilityFilters.some((filter) => {
              if (filter.id === 'availability_in_stock') return inStock;
              if (filter.id === 'availability_out_of_stock') return !inStock;
              return true;
            });
          });
        }

        setProductList(filteredProductList);
        dispatch(setShowLoading({ showLoading: false }));
        return;
      }

      // Handle keyword-only search (no category)
      if (!category && !brand && searchKeyword) {
        // Capitalize first letter of keyword for display
        const capitalizedKeyword =
          searchKeyword.charAt(0).toUpperCase() + searchKeyword.slice(1);
        setCategoryList([
          homeCategory,
          {
            title: capitalizedKeyword,
            link: `#/search?keyword=${encodeURIComponent(searchKeyword)}`,
          },
        ]);
      } else if (category) {
        // Handle category filtering
        const finalDisplayTitle = displayTitle || urlToCategory(category);

        // Build category list from breadcrumb path if available, otherwise use simple structure
        if (breadcrumbPath.length > 0) {
          // Create breadcrumb items with proper links that preserve navigation state
          const breadcrumbItems = breadcrumbPath.map((item, index) => ({
            title: item.title,
            link: item.link,
            // For breadcrumb navigation, we need to preserve the state up to this level
            state: {
              displayTitle: item.title,
              breadcrumbPath: breadcrumbPath.slice(0, index + 1),
            },
          }));

          setCategoryList([homeCategory, ...breadcrumbItems]);
        } else {
          // No breadcrumb path available (direct URL navigation)
          // Build complete breadcrumb path including keyword/subcategory if present
          const effectiveKeyword = subcategory || searchKeyword;
          const completeBreadcrumbPath = await buildCompleteBreadcrumbPath(
            category,
            effectiveKeyword,
          );

          if (completeBreadcrumbPath.length > 0) {
            // Create breadcrumb items with proper links that preserve navigation state
            const breadcrumbItems = completeBreadcrumbPath.map(
              (item, index) => ({
                title: item.title,
                link: item.link,
                state: {
                  displayTitle: item.title,
                  breadcrumbPath: completeBreadcrumbPath.slice(0, index + 1),
                },
              }),
            );

            setCategoryList([homeCategory, ...breadcrumbItems]);
          } else {
            // Fallback to simple breadcrumb if hierarchy not found
            const fallbackItems = [
              {
                title: finalDisplayTitle,
                link: `#/search?category=${category}`,
              },
            ];

            // Add keyword or subcategory if present
            const effectiveKeyword = subcategory || searchKeyword;
            if (effectiveKeyword) {
              const paramName = subcategory ? 'subcategory' : 'keyword';
              fallbackItems.push({
                title: urlToCategory(effectiveKeyword),
                link: `#/search?category=${category}&${paramName}=${effectiveKeyword}`,
              });
            }

            setCategoryList([homeCategory, ...fallbackItems]);
          }
        }
      } else {
        setCategoryList([homeCategory]);
      }

      // Filter out availability filters before sending to backend (they're client-side only)
      const backendFilters = selectedFilters.filter(
        (f) => f.name !== 'availability',
      );

      // Use display title for category, or extract last part of URL key
      const categoryDisplayName =
        displayTitle ||
        (category
          ? category.split('-').pop().charAt(0).toUpperCase() +
            category.split('-').pop().slice(1)
          : category);

      const result = await getProductList(
        categoryDisplayName,
        userInfo.brandId,
        userInfo.idStore,
        userInfo.idCustomer,
        searchKeyword,
        subcategory,
        backendFilters,
        currentPage,
        perPage,
        '01', // Always use price level 01
        sortBy,
        category, // Pass the full URL category string
        menuLevel, // Pass the menu level from navigation state
        signal,
      );

      // Check if request was aborted
      if (signal.aborted) {
        return;
      }

      if (currentPage === 1) {
        // page 가 1일때만 total 나옴
        setTotalProduct(result.total);
      }

      // Filter out products that don't have any image path
      let filteredProductList = result.productList.filter(
        (product) => product.image || product.filepath1,
      );

      // Apply availability filters (client-side)
      const availabilityFilters = selectedFilters.filter(
        (f) => f.name === 'availability',
      );
      if (availabilityFilters.length > 0) {
        filteredProductList = filteredProductList.filter((product) => {
          const inStock = product.avaQty > 0;
          return availabilityFilters.some((filter) => {
            if (filter.id === 'availability_in_stock') return inStock;
            if (filter.id === 'availability_out_of_stock') return !inStock;
            return true;
          });
        });
      }

      setProductList(filteredProductList);
    } catch (error) {
      // Handle abort errors gracefully
      if (
        error.name === 'AbortError' ||
        error.name === 'CanceledError' ||
        error.code === 'ERR_CANCELED'
      ) {
        // Request was cancelled, don't update state
        return;
      }
      console.error('Error fetching product list:', error);
      setProductList([]);
      setTotalProduct(0);
    } finally {
      dispatch(setShowLoading({ showLoading: false }));
    }
  };

  return (
    <Layout>
      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        isOpen={isDrawerOpen}
        onToggle={() => setIsDrawerOpen(!isDrawerOpen)}
        onClose={() => setIsDrawerOpen(false)}
        title='Filters'
      >
        {category && (
          <SearchOptionView
            category={category}
            categoryName={displayTitle}
            selectedFilterList={selectedFilters}
            onSelectedChange={setSelectedFilters}
          />
        )}
      </MobileFilterDrawer>

      <Container>
        {/* Desktop Filter Sidebar */}
        <SearchOptionContainer>
          {category && (
            <SearchOptionView
              category={category}
              categoryName={displayTitle}
              selectedFilterList={selectedFilters}
              onSelectedChange={setSelectedFilters}
            />
          )}
        </SearchOptionContainer>
        <SearchResultContainer>
          <Breadcrumb itemList={categoryList} />
          <TitleContainer>
            <Title>{categoryList[categoryList.length - 1].title}</Title>
            <SearchBar>
              <Input
                placeholder={`Search for ${categoryList[categoryList.length - 1].title}`}
                value={keyword}
                onChange={(e) => {
                  setKeyword(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    navigateToHash('/search', {
                      category,
                      brand,
                      subcategory: subcategory,
                      keyword: keyword.trim() || undefined,
                      page: 1,
                    });
                  }
                }}
              />
              <SearchButton
                onClick={() => {
                  navigateToHash('/search', {
                    category,
                    brand,
                    subcategory: subcategory,
                    keyword: keyword.trim() || undefined,
                    page: 1, // Reset to first page when searching
                  });
                }}
              >
                <FaSearch size={20} color={theme.colors.primaryText} />
              </SearchButton>
            </SearchBar>
          </TitleContainer>
          <TitleContainer>
            <Row>
              <BodyText>Display</BodyText>
              <Dropdown
                style={{ width: '100px' }}
                options={perPageOptions}
                selectedOption={perPage}
                setSelectedOption={setPerPage}
              />
              <BodyText>ITEMS PER PAGE</BodyText>
            </Row>
            <Row>
              <BodyText>SORT BY</BodyText>
              <Dropdown
                style={{ width: '120px' }}
                options={sortOptions}
                selectedOption={sortBy}
                setSelectedOption={setSortBy}
              />
              <RowButton
                onClick={() => {
                  setNumColumns(4);
                }}
              >
                <FaTh size={35} />
              </RowButton>
              <RowButton
                onClick={() => {
                  setNumColumns(1);
                }}
              >
                <FaList size={35} />
              </RowButton>
            </Row>
          </TitleContainer>
          {searchKeyword != '' ? (
            <TotalContainer>{`${totalProduct} products found.`}</TotalContainer>
          ) : null}
          <ListView
            data={productList}
            renderItem={({ item }) => {
              if (numColumns === 1) return <ProductItem data={item} />;
              return <ProductCard data={item} />;
            }}
            numColumns={(width) =>
              numColumns === 1 ? 1 : width <= 480 ? 2 : 4
            }
            rowGap={numColumns === 1 ? '60px' : '30px'}
            columnGap={'10px'}
          />

          <Pagination
            totalPages={Math.ceil(totalProduct / perPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </SearchResultContainer>
      </Container>
    </Layout>
  );
}
